import { User } from '@prisma/client';
import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { FindUserParams } from '@app/domain-interfaces';
import { WorkerDto } from '@app/integ-apigee-interfaces';
import { Axios } from '@app/web-components';

const WORKER_ALL_QUERY_KEY = 'WORKER_ALL_QUERY_KEY';

const WorkerKeys = {
  all: [WORKER_ALL_QUERY_KEY] as const,
  lists: () => [...WorkerKeys.all, 'list'] as const,
  list: (filters: object) => [...WorkerKeys.lists(), filters] as const,
};

const USER_BASE_URL = '/user';

export const useFindUsers = (params: FindUserParams) => {
  const axios = Axios.useAxiosApi();

  return useQuery<User[]>({
    queryKey: ['users', 'list', params],
    queryFn: async () => {
      const { data } = await axios.post(`${USER_BASE_URL}/find`, params);
      return data;
    },
  });
};

export const useFindWorker = (
  enterpriseId?: string,
  options?: Omit<UseQueryOptions<WorkerDto>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<WorkerDto>({
    queryKey: ['users', 'worker', enterpriseId],
    queryFn: async () => {
      const { data } = await axios.get(`/workers/${enterpriseId}`);
      return data;
    },
    enabled: !!enterpriseId && (options?.enabled ?? true),
    ...options,
  });
};

export const useFindWorkers = (enterpriseId: string[] = []) => {
  const axios = Axios.useAxiosApi();

  return useQuery<WorkerDto[]>({
    queryKey: ['users', 'worker', enterpriseId],
    queryFn: async () => {
      const { data } = await axios.get(`/workers/${enterpriseId.join(',')}`);
      return Array.isArray(data) ? data : [data] ?? [];
    },
    enabled: enterpriseId.length > 0,
  });
};

/** use when unable to use a hook */
export const useFindWorkerByEnterpriseIdFetch = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  const findWorkerByEnterpriseId = (enterpriseId: string) => {
    return queryClient.fetchQuery<WorkerDto>({
      queryKey: WorkerKeys.list({ enterpriseId }),
      queryFn: async () => {
        const { data } = await axios.get(`/workers/${enterpriseId}`);
        return data;
      },
    });
  };

  return findWorkerByEnterpriseId;
};
